/**
 * PublicShopBrowser — Unified browse page for all public shops (no auth required).
 * Route: /browse
 *
 * Merges the old PublicShopBrowser (search, filters, categories) with
 * ShopSelector's card design (full-bleed image, blur overlay, preview animation).
 *
 * Data sources:
 *   - /api/shops/public  → shop info (name, address, category, sections)
 *   - /api/datasets       → 3D scan data (camera count, paths for preview animation)
 *   Joined on Dataset.shopId === Shop.id
 *
 * Features:
 *   - Search by name / address
 *   - Category filter chips
 *   - Full-bleed image cards with glassmorphism info overlay
 *   - Preview animation on hover/scroll (cross-fade frame sequence)
 *   - Camera count badge
 *   - SPA navigation to 3D viewer via setCurrentShop()
 *   - "View on Map" toggle → /map
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShopStore, getEnrichedShops, type EnrichedPublicShop } from '../../stores/shopStore'
import { useAppStore } from '@store/appStore'
import { useShopCardPreview } from '@hooks/useShopCardPreview'
import type { ShopMetadata } from '@app-types/shopMetadata'
import { apiUrl, resolveUrl, toBlobUrl } from '../../config'
import { buildMorphUrl, buildMorphUrlFromBase } from '@/utils/buildMorphUrl'
import ViewerChoiceModal from './ViewerChoiceModal'
import './PublicShopBrowser.css'

const CATEGORIES = ['Grocery', 'Electronics', 'Clothing', 'Restaurant', 'Pharmacy', 'Supermarket', 'Cafe', 'Bakery']

interface Props {
  embedded?: boolean
  initialSearch?: string
}

export default function PublicShopBrowser({ embedded = false, initialSearch = '' }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation('shops')
  const { publicShops, publicShopsLoading, publicDatasetsLoading, publicDatasets, fetchPublicShops, fetchPublicDatasets } = useShopStore()
  const setAvailableDatasets = useAppStore((state) => state.setAvailableDatasets)
  const setAvailableShops = useAppStore((state) => state.setAvailableShops)
  const setCurrentShop = useAppStore((state) => state.setCurrentShop)
  const [search, setSearch] = useState(initialSearch)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [pendingShop, setPendingShop] = useState<EnrichedPublicShop | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'reels'>('grid')
  const [filterOpen, setFilterOpen] = useState(false)
  const [sharedShopId, setSharedShopId] = useState<string | null>(null)

  // Filter sheet state
  const [filterNearest, setFilterNearest] = useState(false)
  const [filterMinRating, setFilterMinRating] = useState<number | null>(null)
  const [filterMinReviews, setFilterMinReviews] = useState<number | null>(null)
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null)

  // Sync search when parent updates initialSearch (e.g. home search bar)
  useEffect(() => { setSearch(initialSearch) }, [initialSearch])

  // Track searches (debounced — fires 1s after user stops typing)
  useEffect(() => {
    const q = search.trim()
    if (!q || q.length < 2) return
    const timer = setTimeout(() => {
      import('../../services/activityService').then(({ trackSearch }) => {
        trackSearch(q)
      })
    }, 1000)
    return () => clearTimeout(timer)
  }, [search])

  // Shop metadata for preview animation
  const [shopMetadata, setShopMetadata] = useState<Map<string, ShopMetadata>>(new Map())
  const metadataLoadedRef = useRef<Set<string>>(new Set())

  // Preview animation hook
  const { previewFrame, previousFrame, previewEffects, isPreviewing, currentShopRef, startPreview, stopPreview } = useShopCardPreview()

  // Hover timeout refs for delayed stop
  const hoverTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const shopRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const currentCenterShopRef = useRef<string | null>(null)

  // Fetch both data sources on mount
  useEffect(() => {
    fetchPublicShops()
    fetchPublicDatasets().then(() => {
      // Also populate appStore so useShopCardPreview can find datasets
      const { publicDatasets } = useShopStore.getState()
      setAvailableDatasets(publicDatasets)
      const shopIds = publicDatasets.map(d => d.id)
      setAvailableShops(shopIds)
    })
  }, [])

  // Get enriched shops (joined data)
  const enrichedShops = useMemo(() => getEnrichedShops(), [publicShops, publicDatasets])

  // Client-side filter for instant feedback
  const filtered = useMemo(() => {
    let list = enrichedShops
    if (selectedCategory) {
      list = list.filter((s) => s.category?.toLowerCase() === selectedCategory.toLowerCase())
    }
    if (filterMinRating !== null) {
      list = list.filter((s) => ((s as any).rating ?? 0) >= filterMinRating!)
    }
    if (filterMinReviews !== null) {
      list = list.filter((s) => ((s as any).reviewCount ?? 0) >= filterMinReviews!)
    }
    if (filterNearest && userCoords) {
      list = [...list].sort((a, b) => {
        const dist = (s: EnrichedPublicShop) => {
          const lat = (s as any).latitude
          const lng = (s as any).longitude
          if (lat == null || lng == null) return Infinity
          const dLat = lat - userCoords.lat
          const dLng = lng - userCoords.lng
          return dLat * dLat + dLng * dLng
        }
        return dist(a) - dist(b)
      })
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (s) => s.name.toLowerCase().includes(q) || (s.address && s.address.toLowerCase().includes(q))
      )
    }
    return list
  }, [enrichedShops, search, selectedCategory, filterMinRating, filterMinReviews, filterNearest, userCoords])

  const activeFilterCount =
    (filterNearest ? 1 : 0) +
    (filterMinRating !== null ? 1 : 0) +
    (filterMinReviews !== null ? 1 : 0) +
    (selectedCategory !== null ? 1 : 0)

  const clearAllFilters = () => {
    setFilterNearest(false)
    setFilterMinRating(null)
    setFilterMinReviews(null)
    setSelectedCategory(null)
  }

  const handleNearestToggle = () => {
    const next = !filterNearest
    setFilterNearest(next)
    if (next && !userCoords) {
      navigator.geolocation?.getCurrentPosition(
        (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      )
    }
  }

  // Extract unique categories from actual data for chips
  const availableCategories = useMemo(() => {
    const cats = new Set(publicShops.map((s) => s.category).filter(Boolean))
    return CATEGORIES.filter((c) => cats.has(c)).concat(
      [...cats].filter((c) => !CATEGORIES.includes(c))
    )
  }, [publicShops])

  // Load shop metadata lazily for visible cards (for preview animation)
  const loadMetadataForShop = useCallback(async (shop: EnrichedPublicShop) => {
    if (!shop.dataset || metadataLoadedRef.current.has(shop.id)) return
    metadataLoadedRef.current.add(shop.id)
    try {
      const metadataPath = shop.dataset.paths?.metadata || `/api/scan-data/${shop.dataset.id}/shop_metadata.json`
      const response = await fetch(toBlobUrl(resolveUrl(metadataPath)))
      if (response.ok) {
        const metadata: ShopMetadata = await response.json()
        setShopMetadata(prev => new Map(prev).set(shop.dataset!.id, metadata))
      }
    } catch (error) {
      console.warn(`[PublicShopBrowser] Failed to load metadata for ${shop.id}:`, error)
    }
  }, [])

  // IntersectionObserver to lazy-load metadata for visible cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const shopId = entry.target.getAttribute('data-shop-id')
            if (shopId) {
              const shop = enrichedShops.find(s => s.id === shopId)
              if (shop) loadMetadataForShop(shop)
            }
          }
        }
      },
      { rootMargin: '200px' }
    )
    shopRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [enrichedShops, loadMetadataForShop])

  // Scroll-based auto-play: start preview for card closest to viewport center
  useEffect(() => {
    if (shopRefs.current.size === 0) return

    const findCardInCenter = (): string | null => {
      const viewportCenter = window.innerHeight / 2
      let closestId: string | null = null
      let closestDist = Infinity
      shopRefs.current.forEach((el, id) => {
        const rect = el.getBoundingClientRect()
        const dist = Math.abs(rect.top + rect.height / 2 - viewportCenter)
        if (dist < closestDist) { closestDist = dist; closestId = id }
      })
      return closestId
    }

    const handleScroll = () => {
      const centerShop = findCardInCenter()
      if (centerShop === currentCenterShopRef.current) return
      currentCenterShopRef.current = centerShop
      if (centerShop) {
        const shop = enrichedShops.find(s => s.id === centerShop)
        const datasetId = shop?.dataset?.id
        if (datasetId && shopMetadata.has(datasetId)) {
          startPreview(datasetId, shopMetadata)
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [enrichedShops, shopMetadata, startPreview])

  const handleShopClick = useCallback((shop: EnrichedPublicShop) => {
    if (shop.has3DViewer && shop.dataset) {
      setPendingShop(shop)
    } else if (shop.has3DViewer) {
      // Scanner-uploaded shop — open morph viewer in-place (no page reload)
      const firstSection = (shop.sections ?? []).find((s: any) => s.camerasJsonUrl)
      if (firstSection?.camerasJsonUrl) {
        const base = firstSection.camerasJsonUrl.replace(/\/cameras\.json$/, '/')
        buildMorphUrlFromBase(base).then((url) => {
          navigate(`/?morphUrl=${encodeURIComponent(url)}`)
        }).catch(() => navigate(`/shop/${shop.id}`))
      } else {
        navigate(`/shop/${shop.id}`)
      }
    } else {
      navigate(`/shop/${shop.id}`)
    }
  }, [navigate])

  const handleOriginalViewer = useCallback(() => {
    if (!pendingShop?.dataset) return
    setCurrentShop(pendingShop.dataset.id)
    window.location.href = `/?shopid=${pendingShop.dataset.id}`
  }, [pendingShop, setCurrentShop])

  const handleMorphViewer = useCallback(async () => {
    if (!pendingShop?.dataset) return
    const url = await buildMorphUrl(pendingShop.dataset)
    window.location.href = url
  }, [pendingShop])

  const isLoading = publicShopsLoading || publicDatasetsLoading

  // Build morph viewer share URL for a shop
  const buildShareUrl = useCallback(async (shop: EnrichedPublicShop): Promise<string> => {
    if (!shop.dataset) return window.location.origin
    const paths = shop.dataset.paths
    const camerasPath = paths.cameras || `/api/scan-data/${shop.dataset.id}/section_0/cameras.json`
    const apiPrefix = '/api/scan-data/'
    const blobRelative = camerasPath.startsWith(apiPrefix)
      ? camerasPath.substring(apiPrefix.length, camerasPath.lastIndexOf('/') + 1)
      : camerasPath.substring(0, camerasPath.lastIndexOf('/') + 1)
    try {
      const res = await fetch(apiUrl('/api/scan-data-token'))
      if (res.ok) {
        const { base_url, sas_token } = await res.json()
        const base = `${base_url}/${blobRelative}`
        const params = new URLSearchParams({ base, sas: sas_token })
        return `${window.location.origin}/morph-viewer/morph-viewer-v2.html?${params.toString()}`
      }
    } catch { /* fall through */ }
    const base = apiUrl(`${apiPrefix}${blobRelative}`)
    return `${window.location.origin}/morph-viewer/morph-viewer-v2.html?base=${encodeURIComponent(base)}`
  }, [])

  const handleShareShop = useCallback(async (e: React.MouseEvent, shop: EnrichedPublicShop) => {
    e.stopPropagation()
    setSharedShopId(shop.id)
    try {
      const url = await buildShareUrl(shop)
      if (navigator.share) {
        await navigator.share({ title: shop.name, url })
      } else {
        await navigator.clipboard.writeText(url)
        // brief visual confirm — reset after 2s
        setTimeout(() => setSharedShopId(null), 2000)
        return
      }
    } catch { /* cancelled */ }
    setSharedShopId(null)
  }, [buildShareUrl])

  return (
    <div className={`shop-browser${embedded ? ' shop-browser--embedded' : ''}`}>
      {/* Header — hidden in embedded mode */}
      <header className="shop-browser-header">
        <button className="shop-browser-home" onClick={() => navigate('/')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
          Home
        </button>
        <h1>{t('browseShops')}</h1>
        <button className="shop-browser-map-btn" onClick={() => navigate('/map')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M15 3l-6 2.25L3 3v18l6-2.25L15 21l6-2.25V3L15 3zm0 15.4l-6-2.25V5.6l6 2.25v10.55z"/></svg>
          Map
        </button>
      </header>

      {/* Search */}
      <div className="shop-browser-search-bar">
        <input
          type="text"
          placeholder={t('searchShopsPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="shop-browser-search"
        />
      </div>

      {/* ── Toolbar: view toggle + filter ── */}
      <div className="shop-browser-toolbar">
        <div className="shop-browser-view-toggle">
          <button
            className={`shop-browser-view-btn${viewMode === 'grid' ? ' active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
            </svg>
          </button>
          <button
            className={`shop-browser-view-btn${viewMode === 'reels' ? ' active' : ''}`}
            onClick={() => setViewMode('reels')}
            title="Reels view"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 5a1 1 0 0 1 1-1h14a1 1 0 0 1 0 2H5a1 1 0 0 1-1-1zm0 7a1 1 0 0 1 1-1h14a1 1 0 0 1 0 2H5a1 1 0 0 1-1-1zm0 7a1 1 0 0 1 1-1h14a1 1 0 0 1 0 2H5a1 1 0 0 1-1-1z"/>
            </svg>
          </button>
        </div>
        <button
          className={`shop-browser-filter-btn${filterOpen ? ' active' : ''}${activeFilterCount > 0 ? ' has-filter' : ''}`}
          onClick={() => setFilterOpen(true)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 4a1 1 0 0 1 1-1h16a1 1 0 0 1 .8 1.6l-6 8v5.4a1 1 0 0 1-.55.9l-4 2A1 1 0 0 1 9 20v-7.4L3.2 4.6A1 1 0 0 1 3 4z"/>
          </svg>
          Filters
          {activeFilterCount > 0 && <span className="shop-browser-filter-badge">{activeFilterCount}</span>}
        </button>
      </div>

      {/* Filter bottom-sheet modal */}
      {filterOpen && (
        <div className="filter-sheet-overlay" onClick={() => setFilterOpen(false)}>
          <div className="filter-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">Filter Stores</span>
              {activeFilterCount > 0 && (
                <button className="filter-sheet-clear" onClick={clearAllFilters}>Clear all</button>
              )}
            </div>

            {/* Sort by */}
            <p className="filter-sheet-section-label">SORT BY</p>
            <button
              className={`filter-toggle-row${filterNearest ? ' active' : ''}`}
              onClick={handleNearestToggle}
            >
              <span className="filter-toggle-emoji">📍</span>
              <div className="filter-toggle-text">
                <span className="filter-toggle-label">Nearest to me</span>
                <span className="filter-toggle-sub">Sort stores by distance</span>
              </div>
              <div className={`filter-toggle-switch${filterNearest ? ' on' : ''}`}>
                <div className={`filter-toggle-thumb${filterNearest ? ' on' : ''}`} />
              </div>
            </button>

            {/* Category */}
            {availableCategories.length > 0 && (
              <>
                <p className="filter-sheet-section-label">CATEGORY</p>
                <div className="filter-chips-row">
                  {availableCategories.map((cat) => (
                    <button
                      key={cat}
                      className={`filter-chip${selectedCategory === cat ? ' active' : ''}`}
                      onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Minimum rating */}
            <p className="filter-sheet-section-label">MINIMUM RATING</p>
            <div className="filter-chips-row">
              {[4, 3, 2].map((r) => (
                <button
                  key={r}
                  className={`filter-chip${filterMinRating === r ? ' active' : ''}`}
                  onClick={() => setFilterMinRating(filterMinRating === r ? null : r)}
                >
                  {'★'.repeat(r)}&nbsp;{r}+
                </button>
              ))}
            </div>

            {/* Minimum reviews */}
            <p className="filter-sheet-section-label">MINIMUM REVIEWS</p>
            <div className="filter-chips-row">
              {[10, 50, 100].map((n) => (
                <button
                  key={n}
                  className={`filter-chip${filterMinReviews === n ? ' active' : ''}`}
                  onClick={() => setFilterMinReviews(filterMinReviews === n ? null : n)}
                >
                  {n}+ reviews
                </button>
              ))}
            </div>

            <button
              className="filter-apply-btn"
              style={{ opacity: activeFilterCount > 0 ? 1 : 0.55 }}
              onClick={() => setFilterOpen(false)}
            >
              Show {filtered.length} store{filtered.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      <div className="shop-browser-body">
        {isLoading ? (
          <div className="shop-browser-loading">
            <div className="shop-browser-spinner" />
            {t('loadingShops')}
          </div>
        ) : filtered.length === 0 ? (
          <div className="shop-browser-empty">
            {publicShops.length === 0 ? t('noPublicShopsYet') : t('noShopsMatchSearch')}
          </div>
        ) : viewMode === 'reels' ? (
          <div className="shop-browser-reels">
            {filtered.map((shop) => (
              <ShopCard
                key={shop.id}
                shop={shop}
                shopRefs={shopRefs}
                isPreviewing={isPreviewing}
                previewFrame={previewFrame}
                previousFrame={previousFrame}
                previewEffects={previewEffects}
                currentPreviewShop={currentShopRef.current}
                shopMetadata={shopMetadata}
                hoverTimeoutRef={hoverTimeoutRef}
                startPreview={startPreview}
                stopPreview={stopPreview}
                onClick={() => handleShopClick(shop)}
                reelsMode
                onShare={(e) => handleShareShop(e, shop)}
                isShared={sharedShopId === shop.id}
              />
            ))}
          </div>
        ) : (
          <div className="shop-browser-grid">
            {filtered.map((shop) => (
              <ShopCard
                key={shop.id}
                shop={shop}
                shopRefs={shopRefs}
                isPreviewing={isPreviewing}
                previewFrame={previewFrame}
                previousFrame={previousFrame}
                previewEffects={previewEffects}
                currentPreviewShop={currentShopRef.current}
                shopMetadata={shopMetadata}
                hoverTimeoutRef={hoverTimeoutRef}
                startPreview={startPreview}
                stopPreview={stopPreview}
                onClick={() => handleShopClick(shop)}
                onShare={(e) => handleShareShop(e, shop)}
                isShared={sharedShopId === shop.id}
              />
            ))}
          </div>
        )}
      </div>
      {pendingShop && (
        <ViewerChoiceModal
          shopName={pendingShop.name}
          onOriginal={handleOriginalViewer}
          onMorph={handleMorphViewer}
          onClose={() => setPendingShop(null)}
        />
      )}
    </div>
  )
}

/* ── ShopCard: Full-bleed image with glassmorphism info overlay ── */

interface ShopCardProps {
  shop: EnrichedPublicShop
  shopRefs: React.MutableRefObject<Map<string, HTMLDivElement>>
  isPreviewing: boolean
  previewFrame: string | null
  previousFrame: string | null
  previewEffects: { currentOpacity?: number; previousOpacity?: number }
  currentPreviewShop: string | null
  shopMetadata: Map<string, ShopMetadata>
  hoverTimeoutRef: React.MutableRefObject<Map<string, ReturnType<typeof setTimeout>>>
  startPreview: (shopId: string, metadata: Map<string, ShopMetadata>) => void
  stopPreview: () => void
  onClick: () => void
  reelsMode?: boolean
  onShare?: (e: React.MouseEvent) => void
  isShared?: boolean
}

function ShopCard({
  shop, shopRefs, isPreviewing, previewFrame, previousFrame,
  previewEffects, currentPreviewShop, shopMetadata, hoverTimeoutRef,
  startPreview, stopPreview, onClick, reelsMode = false, onShare, isShared = false,
}: ShopCardProps) {
  const { t } = useTranslation('shops')
  const posterUrl = apiUrl(`/api/shops/${shop.id}/poster`)
  const [imgError, setImgError] = useState(false)
  const datasetId = shop.dataset?.id

  // Thumbnail error fallback chain: poster → thumbnail.jpg → frame_0000.jpg → placeholder
  const handleThumbnailError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const currentSrc = img.src
    if (currentSrc.includes('/poster') && shop.dataset) {
      const basePath = shop.dataset.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shop.dataset.id}`
      img.src = toBlobUrl(resolveUrl(`${basePath}/thumbnail.jpg?shop=${shop.dataset.id}`))
    } else if (currentSrc.includes('thumbnail.jpg') && shop.dataset) {
      const basePath = shop.dataset.paths?.metadata?.replace('/shop_metadata.json', '') || `/api/scan-data/${shop.dataset.id}`
      img.src = toBlobUrl(resolveUrl(`${basePath}/section_0/frames/frame_0000.jpg`))
    } else {
      setImgError(true)
    }
  }

  const isThisCardPreviewing = isPreviewing && datasetId && currentPreviewShop === datasetId

  return (
    <div
      ref={(el) => {
        if (el) shopRefs.current.set(shop.id, el)
      }}
      className={`shop-card${reelsMode ? ' shop-card--reel' : ''}`}
      data-shop-id={shop.id}
      onClick={onClick}
      onMouseEnter={() => {
        if (!datasetId) return
        const existingTimeout = hoverTimeoutRef.current.get(datasetId)
        if (existingTimeout) clearTimeout(existingTimeout)
        if (shopMetadata.has(datasetId)) {
          startPreview(datasetId, shopMetadata)
        }
      }}
      onMouseLeave={() => {
        if (!datasetId) return
        const timeout = setTimeout(() => {
          stopPreview()
          hoverTimeoutRef.current.delete(datasetId)
        }, 500)
        hoverTimeoutRef.current.set(datasetId, timeout)
      }}
      role="button"
      tabIndex={0}
      aria-label={`${shop.name}${shop.cameraCount ? ` — ${shop.cameraCount} cameras` : ''}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() }
      }}
    >
      {/* Full-bleed background image */}
      <div className="shop-card-thumbnail-container">
        {!imgError ? (
          <img
            className="shop-card-img"
            src={posterUrl}
            alt={`${shop.name} thumbnail`}
            loading="lazy"
            onError={handleThumbnailError}
          />
        ) : (
          <div className="shop-card-img-placeholder">🏪</div>
        )}

        {/* Preview animation overlay (cross-fade between frames) */}
        {isThisCardPreviewing && (
          <>
            {previousFrame && (
              <img
                className="shop-card-preview-frame"
                src={previousFrame}
                alt=""
                style={{ opacity: previewEffects.previousOpacity ?? 0, zIndex: 10 }}
              />
            )}
            {previewFrame && (
              <img
                className="shop-card-preview-frame"
                src={previewFrame}
                alt=""
                style={{ opacity: previewEffects.currentOpacity ?? 1, zIndex: 11 }}
              />
            )}
          </>
        )}

        {/* Gradient overlay for text readability */}
        <div className="shop-card-overlay" />

        {/* Camera count badge */}
        {shop.cameraCount != null && (
          <div className="shop-card-badge">
            <svg className="shop-card-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{shop.cameraCount}</span>
          </div>
        )}

        {/* "Coming soon" indicator for shops without 3D data */}
        {!shop.has3DViewer && (
          <div className="shop-card-coming-soon">{t('comingSoon')}</div>
        )}

        {/* Share button */}
        {onShare && shop.has3DViewer && (
          <button
            className={`shop-card-share-btn${isShared ? ' copied' : ''}`}
            onClick={onShare}
            title="Share morph viewer link"
          >
            {isShared ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {/* Info overlay (blurred background at bottom) */}
      <div className="shop-card-info">
        <div className="shop-card-name">{shop.name}</div>
        <div className="shop-card-meta">
          {shop.category && <span className="shop-card-category">{shop.category}</span>}
          <span className="shop-card-sections">
            {t('sectionCount', { count: shop.sections?.length || 0 })}
          </span>
        </div>
      </div>
    </div>
  )
}
